import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import * as moment from 'moment-timezone';
import Product, {
  HEIGHT_EQUIPMENT_OPTIONS,
} from 'src/app/model/product/product';
import Windowsticker from 'src/app/model/product/windowsticker/windowsticker';
import CustomWindowsticker from 'src/app/model/product/windowsticker/custom-windowsticker';
import Board from 'src/app/model/product/board/board';
import OtherProduct from 'src/app/model/product/other-product/other-product';
import ProductHelper, {
  BoardPartDisplay,
  CustomStickerPartDisplay,
  StandardStickerPartDisplay,
} from 'src/app/helpers/product-helper';
import { GeneralHelper } from 'src/app/helpers/general-helper';
import { CollectionHelper } from 'src/app/helpers/collection-helper';
import { Lightbox } from 'ngx-lightbox';
import WindowStickerPlacement from 'src/app/model/product/windowsticker/windowsticker-placement';
import BoardPlacement, {
  DistanceToBottomChoiceArray,
} from 'src/app/model/product/board/board-placement';
import OtherProductPlacement from 'src/app/model/product/other-product/other-product-placement';
import ProductAsset from 'src/app/model/product-asset';
import { LocationVerificationProjectWideDisplay } from '../location-verification-project-wide-display';
import Employee from 'src/app/model/product/employee';
import { SubscriptionBasedComponent } from 'src/app/subscription-helpers/subscription-based.component';
import { EmployeeChoicesService } from 'src/app/services/employee-choices.service';
import { DatePipe } from '@angular/common';
import { ProjectDisplayObservableService } from 'src/app/services/project-display-observable.service';
import { ModalService } from 'src/app/services/modal.service';
import DateHelper from 'src/app/helpers/date-helper';
import { FileHelper } from '../../../helpers/file-helper';

@Component({
  selector: 'location-verification-product-display',
  templateUrl: './location-verification-product-display.component.html',
  styleUrls: ['./location-verification-product-display.component.scss'],
})
export class LocationVerificationProductDisplayComponent
  extends SubscriptionBasedComponent
  implements OnInit
{
  @ViewChild('actualPlacementInput')
  public actualPlacementInput: ElementRef<HTMLInputElement>;

  @Input()
  product: Product;
  @Input()
  index: number;
  @Input()
  withPlacement: boolean;
  @Input()
  multipleProducts: boolean = false;
  @Input()
  locationVerificationProjectWideDisplay: LocationVerificationProjectWideDisplay;
  @Output()
  locationAssetsUpdated = new EventEmitter<any>();

  windowSticker: Windowsticker;
  standardWindowSticker: Windowsticker;
  customWindowSticker: CustomWindowsticker;
  board: Board;
  otherProduct: OtherProduct;
  productLabel: string;

  multipleProductParts: boolean = false;
  standardStickerPartDisplays: StandardStickerPartDisplay[];
  customStickerPartDisplays: CustomStickerPartDisplay[];
  boardPartDisplays: BoardPartDisplay[];
  designFormSection: string;

  designAsset: ProductAsset;
  designAlbum: LightboxItem[];
  designLocationAsset: ProductAsset;
  designLocationAlbum: LightboxItem[];

  requestedDateDisplay: string;
  placerChoices: Employee[];
  externalSupplierEmployeeChoices: Employee[];
  hours: string[] = [];
  minutes: string[] = [];

  heightEquipmentOptions = HEIGHT_EQUIPMENT_OPTIONS;
  distanceToBottomOptions = DistanceToBottomChoiceArray;

  isImage = FileHelper.isImage;

  constructor(
    private employeeChoicesService: EmployeeChoicesService,
    private projectDisplayObservableService: ProjectDisplayObservableService,
    private datePipe: DatePipe,
    private modalService: ModalService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.preprocessProduct();
    this.calculateRequestedDateDisplay();
    this.hours = GeneralHelper.hours;
    this.minutes = GeneralHelper.minutes;
    this.addSubscription(
      this.employeeChoicesService.getPlacerChoicesSubscribable(),
      (placers: Employee[]) => {
        this.placerChoices = placers;
      }
    );
    this.addSubscription(
      this.employeeChoicesService.getExternalSupplierChoicesSubscribable(),
      (externalSupplierEmployees: Employee[]) => {
        this.externalSupplierEmployeeChoices = externalSupplierEmployees;
      }
    );
    this.addSubscription(
      this.projectDisplayObservableService.getProjectPlacementRequestDateChangedSubscribable(),
      () => {
        this.calculateRequestedDateDisplay();
      }
    );
  }

  preprocessProduct() {
    if (ProductHelper.isWindowSticker(this.product)) {
      this.windowSticker = <Windowsticker>this.product;
      this.standardWindowSticker = <Windowsticker>this.product;
      this.preprocessWindowSticker();
      this.preprocessStandardWindowSticker();
    }
    if (ProductHelper.isCustomWindowSticker(this.product)) {
      this.windowSticker = <CustomWindowsticker>this.product;
      this.customWindowSticker = <CustomWindowsticker>this.product;
      this.preprocessWindowSticker();
      this.preprocessCustomWindowSticker();
    }
    if (ProductHelper.isBoard(this.product)) {
      this.board = <Board>this.product;
      this.preprocessBoard();
    }
    if (ProductHelper.isOtherProduct(this.product)) {
      this.otherProduct = <OtherProduct>this.product;
      this.preprocessOtherProduct();
    }
    this.productLabel = ProductHelper.getTypeOf(this.product);
    this.preprocessAssets();
  }

  preprocessWindowSticker() {
    this.designFormSection = 'windowDesign';
  }

  preprocessStandardWindowSticker() {
    this.multipleProductParts =
      this.windowSticker.standardStickerParts != null &&
      this.windowSticker.standardStickerParts.length > 1;
    this.standardStickerPartDisplays =
      ProductHelper.calculateStandardStickerPartDisplays(this.windowSticker);
  }

  preprocessCustomWindowSticker() {
    this.multipleProductParts =
      this.customWindowSticker.stickerParts != null &&
      this.customWindowSticker.stickerParts.length > 1;
    this.customStickerPartDisplays =
      ProductHelper.calculateCustomStickerPartDisplays(
        this.customWindowSticker
      );
  }

  preprocessBoard() {
    this.designFormSection = 'boardDesign';
    if (CollectionHelper.isPopulated(this.board.boardParts)) {
      this.multipleProductParts = this.board.boardParts.length > 1;
      this.boardPartDisplays = [];
      this.board.boardParts.forEach((bp, i) => {
        this.boardPartDisplays.push(
          ProductHelper.calculateBoardPartDisplay(bp, i)
        );
      });
    }
  }

  preprocessOtherProduct() {
    this.designFormSection = 'otherDesign';
  }

  preprocessAssets() {
    if (CollectionHelper.isPopulated(this.product.productAssets)) {
      if (this.designAlbum == null) {
        this.designAlbum = [];
      } else {
        this.designAlbum.length = 0;
      }
      if (this.designLocationAlbum == null) {
        this.designLocationAlbum = [];
      } else {
        this.designLocationAlbum.length = 0;
      }
      this.product.productAssets.forEach((pa) => {
        if (!pa.archived && pa.asset != null) {
          if (pa.formSection === 'internalDesign') {
            this.designAsset = this.designAsset == null ? pa : this.designAsset;
            this.designAlbum.push({
              src: `/api/assets/${pa.asset.uuid}`,
              caption: '',
              thumb: `/api/assets/${pa.asset.uuid}`,
            });
          } else if (ProductHelper.isDesignLocationAsset(pa)) {
            this.designLocationAsset =
              this.designLocationAsset == null ? pa : this.designLocationAsset;
            this.designLocationAlbum.push({
              src: `/api/assets/${pa.asset.uuid}`,
              caption: '',
              thumb: `/api/assets/${pa.asset.uuid}`,
            });
          }
        }
      });
    }
  }

  calculateRequestedDateDisplay() {
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement ==
      null
    ) {
      this.requestedDateDisplay = '';
      return;
    }
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .placementEarliestDate == null
    ) {
      this.requestedDateDisplay = this.locationVerificationProjectWideDisplay
        .subProject.projectPlacement.placeASAP
        ? 'ASAP'
        : '';
      return;
    }
    this.requestedDateDisplay = this.datePipe.transform(
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .placementEarliestDate,
      'dd/MM/yyyy'
    );
  }

  calculateDesignLocationSection(): string {
    if (this.windowSticker != null) {
      return 'windowDesign';
    }
    if (this.board != null) {
      return 'boardDesign';
    }
    if (this.otherProduct != null) {
      return 'otherDesign';
    }
  }

  extraInfoAddressChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.extraInfoAddress =
      value;
  }

  extraInfoSiteChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.extraInfoSite =
      value;
  }

  extraPlacementInfoChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.extraPlacementInfo =
      value;
  }

  windowObstacleChanged(value: boolean) {
    (<WindowStickerPlacement>(
      this.windowSticker.productPlacement
    )).windowObstacle = value;
  }

  extraInfoWindowObstacleChanged(value: string) {
    (<WindowStickerPlacement>(
      this.windowSticker.productPlacement
    )).extraInfoWindowObstacle = value;
  }

  oldStickerPresentChanged(value: boolean) {
    (<WindowStickerPlacement>(
      this.windowSticker.productPlacement
    )).oldStickerPresent = value;
  }

  extraInfoOldStickerPresentChanged(value: string) {
    (<WindowStickerPlacement>(
      this.windowSticker.productPlacement
    )).extraInfoOldStickerPresent = value;
  }

  alignmentChanged(value: string) {
    (<WindowStickerPlacement>this.windowSticker.productPlacement).alignment =
      value;
  }

  insideChanged(value: boolean) {
    (<WindowStickerPlacement>this.windowSticker.productPlacement).inside =
      value;
  }

  wetChanged(value: boolean) {
    (<WindowStickerPlacement>this.windowSticker.productPlacement).wet = value;
  }

  placeInstructionsChanged(value: string) {
    this.board.placeInstructions = value;
  }

  distanceFrontPostToStreetChanged(value: string) {
    this.board.distanceFrontPostToStreet = value;
  }

  newWoodPostsChanged(value: boolean) {
    (<BoardPlacement>this.board.productPlacement).newWoodPosts = value;
  }

  boardObstaclesPresentChanged(value: boolean) {
    (<BoardPlacement>this.board.productPlacement).obstaclesPresent = value;
  }

  boardObstaclesInfoTextChanged(value: string) {
    (<BoardPlacement>this.board.productPlacement).obstaclesInfo = value;
  }

  nearbyParkingChanged(value: boolean) {
    this.locationVerificationProjectWideDisplay.nearbyParkingValue = value;
    this.setBoardPlacementProperty('canParkNearby', value);
  }

  nearbyParkingInfoChanged(value: string) {
    this.locationVerificationProjectWideDisplay.nearbyParkingInfo = value;
    this.setBoardPlacementProperty('nearbyParkingInfo', value);
  }

  private setBoardPlacementProperty(property: string, value: any) {
    if (
      !CollectionHelper.isPopulated(
        this.locationVerificationProjectWideDisplay.project.products
      )
    ) {
      return;
    }
    this.locationVerificationProjectWideDisplay.project.products.forEach(
      (p) => {
        if (ProductHelper.isBoard(p)) {
          (<BoardPlacement>p.productPlacement)[property] = value;
        }
      }
    );
  }

  otherProductObstaclesPresentChanged(value: boolean) {
    (<OtherProductPlacement>(
      this.otherProduct.productPlacement
    )).obstaclesPresent = value;
  }

  otherProductObstaclesInfoTextChanged(value: string) {
    (<OtherProductPlacement>this.otherProduct.productPlacement).obstaclesInfo =
      value;
  }

  installerInfoChanged(value: string) {
    this.product.productPlacement.installerInfo = value;
  }

  extraHoogwerkerChanged(value: boolean) {
    this.product.productPlacement.extraHoogwerker = value;
  }

  extraHoogwerkerDescriptionChanged(value: string) {
    this.product.productPlacement.extraHoogwerkerDescription = value;
  }

  extraHoogwerkerDoneChanged(value: boolean) {
    this.product.productPlacement.extraHoogwerkerDone = value;
  }

  extraHoogwerkerDeadlineChange(event) {
    this.product.productPlacement.extraHoogwerkerDeadline = event.value;
  }

  parkingBanChanged(value: boolean) {
    this.product.productPlacement.parkingBan = value;
  }

  extraInfoParkingBanChanged(value: string) {
    this.product.productPlacement.extraInfoParkingBan = value;
  }

  parkingBanDoneChanged(value: boolean) {
    this.product.productPlacement.parkingBanDone = value;
  }

  parkingBanDeadlineChange(event) {
    this.product.productPlacement.parkingBanDeadline = event.value;
  }

  needExtraSupplierChanged(value: boolean) {
    this.product.productPlacement.needExtraSupplier = value;
  }

  extraInfoNeedExtraSupplierChanged(value: string) {
    this.product.productPlacement.extraInfoNeedExtraSupplier = value;
  }

  needExtraSupplierDoneChanged(value: boolean) {
    this.product.productPlacement.needExtraSupplierDone = value;
  }

  extraSupplierDeadlineChange(event) {
    this.product.productPlacement.extraSupplierDeadline = event.value;
  }

  needExtraMaterialsChanged(value: boolean) {
    this.product.productPlacement.needExtraMaterials = value;
  }

  extraInfoNeedExtraMaterialsChanged(value: string) {
    this.product.productPlacement.extraInfoNeedExtraMaterials = value;
  }

  needExtraMaterialsDoneChanged(value: boolean) {
    this.product.productPlacement.needExtraMaterialsDone = value;
  }

  extraMaterialsDeadlineChange(event) {
    this.product.productPlacement.extraMaterialsDeadline = event.value;
  }

  inpressLogoChanged(value: boolean) {
    this.product.inpressLogo = value;
  }

  immoscoopLogoChanged(value: boolean) {
    this.product.immoscoopLogo = value;
  }

  spottoLogoChanged(value: boolean) {
    this.product.spottoLogo = value;
  }

  collectKeyChanged(value: boolean) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.collectKey =
      value;
  }

  collectKeyExtraInfoChange(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.collectKeyExtraInfo =
      value;
  }

  undergroundPipeworkChanged(value: boolean) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.undergroundPipework =
      value;
  }

  undergroundPipeworkTextChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.undergroundPipeworkText =
      value;
  }

  appointmentChanged(value: boolean) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.appointment =
      value;
  }

  needAppointmentInfoChanged(value: string) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.needAppointmentInfo =
      value;
  }

  placeASAP(event) {
    this.locationVerificationProjectWideDisplay.subProject.projectPlacement.placeASAP =
      event;
    if (event) {
      delete this.locationVerificationProjectWideDisplay.subProject
        .projectPlacement.placementEarliestDate;
    }
    this.projectDisplayObservableService
      .getProjectPlacementRequestDateChangedSubscribable()
      .next();
  }

  placementEarliestDateChange(event) {
    if (event != null) {
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement.placeASAP =
        false;
    }
    this.projectDisplayObservableService
      .getProjectPlacementRequestDateChangedSubscribable()
      .next();
  }

  copyProjectPlacementDate() {
    let dateCopy = new Date(
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement.placementEarliestDate
    );
    this.product.productPlacement.actualPlacementDate = dateCopy;
  }

  actualPlacementDateChange(event) {
    let newDate = event.value;
    if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .placementEarliestDate != null &&
      moment
        .tz(newDate, 'Europe/Brussels')
        .isBefore(
          moment.tz(
            this.locationVerificationProjectWideDisplay.subProject
              .projectPlacement.placementEarliestDate,
            'Europe/Brussels'
          ),
          'day'
        )
    ) {
      this.modalService.confirmDialog(
        'Datum vroeger dan gevraagde datum',
        'De geselecteerde datum (' +
          DateHelper.formatDate(newDate) +
          ') is vroeger dan de gevraagde datum (' +
          DateHelper.formatDate(
            this.locationVerificationProjectWideDisplay.subProject
              .projectPlacement.placementEarliestDate
          ) +
          '). Bent u zeker dat u deze datum wilt selecteren?',
        (c) => {
          if (c) {
            if (
              this.locationVerificationProjectWideDisplay.subProject
                .projectPlacement.appointment &&
              this.product.productPlacement.actualPlacementDate != null
            ) {
              this.checkAppointmentPlacement(newDate);
            } else {
              this.product.productPlacement.actualPlacementDate = newDate;
            }
          } else {
            this.actualPlacementInput.nativeElement.value =
              DateHelper.formatDate(
                this.product.productPlacement.actualPlacementDate
              );
          }
        }
      );
    } else if (
      this.locationVerificationProjectWideDisplay.subProject.projectPlacement
        .appointment &&
      this.product.productPlacement.actualPlacementDate != null
    ) {
      this.checkAppointmentPlacement(newDate);
    } else {
      this.product.productPlacement.actualPlacementDate = newDate;
    }
  }

  checkAppointmentPlacement(newDate) {
    this.modalService.confirmDialog(
      'Opgelet!',
      'Er is een afspraak nodig voor deze plaatsing. Contacteer de klant om deze ook te verzetten indien u de plaatsingsdatum aanpast.',
      (c) => {
        if (c) {
          this.product.productPlacement.actualPlacementDate = newDate;
        } else {
          this.actualPlacementInput.nativeElement.value = DateHelper.formatDate(
            this.product.productPlacement.actualPlacementDate
          );
        }
      }
    );
  }

  actualPlacementDateConfirmedChange(value) {
    this.product.productPlacement.actualPlacementDateConfirmed = value;
  }

  designLocationDeleted() {
    this.designLocationAsset = null;
    this.preprocessAssets();
  }

  openDesignLightbox() {
    this._lightbox.open(this.designAlbum);
  }

  openDesignLocationLightbox() {
    this._lightbox.open(this.designLocationAlbum);
  }

  openSitePhotosLightbox() {
    this._lightbox.open(
      this.locationVerificationProjectWideDisplay.locationAlbum
    );
  }

  openTopdownLocationLightbox() {
    this._lightbox.open(
      this.locationVerificationProjectWideDisplay.topdownLocationAlbum
    );
  }

  compareEnums(e1, e2) {
    return e1 != null && e2 != null && e1.name === e2.name;
  }

  compareEntities(o1, o2) {
    return GeneralHelper.compareEntities(o1, o2);
  }

  compareNumbers(n1, n2) {
    return n1 == n2;
  }

  compareOrdinalEnum(o1: any, o2: any): boolean {
    return GeneralHelper.compareOrdinalEnum(o1, o2);
  }

  ordinalEnumGetter(o: any): string {
    return GeneralHelper.ordinalEnumGetter(o);
  }

  trackById(index: number, obj: any): number {
    return GeneralHelper.trackById(index, obj);
  }
}

interface LightboxItem {
  src: string;
  caption: string;
  thumb: string;
}
